import React from 'react';
import { IHeadToHeadSummary } from '../models/head-to-head';
import { H4, Label, Muted } from './text';

interface IProps {
	summary: IHeadToHeadSummary;
	homeTeamName: string;
	awayTeamName: string;
}

/**
 * HeadToHeadSummary Component
 *
 * Displays aggregated summary statistics showing total wins, draws,
 * and total matches between two teams in the current competition.
 */
export default function HeadToHeadSummary({ summary, homeTeamName, awayTeamName }: IProps) {
	return (
		<div className="p-6 mb-6">
			<H4 className="text-center mb-4">Onderlinge resultaten</H4>

			<div className="max-w-2xl m-auto pt-4 grid grid-cols-3 gap-4">
				{/* Home Team Wins */}
				<div className="text-center flex flex-col items-center gap-y-2">
					<span className="bg-red-500 shadow-[inset_0_0_0_2px_#E2002B] py-1 px-2.5 inline-block rounded-md">
						<Label className="text-white leading-none text-base lg:text-base">{summary.homeWins}</Label>
					</span>

					<Muted>Overwinningen</Muted>
					<Muted className="-mt-1">{homeTeamName}</Muted>
				</div>

				{/* Draws */}
				<div className="text-center flex flex-col items-center gap-y-2">
					<span className="bg-transparent shadow-[inset_0_0_0_2px_#1C1A27] py-1 px-2.5 inline-block rounded-md">
						<Label className="text-black-500 leading-none text-base lg:text-base">{summary.draws}</Label>
					</span>
					<Muted>Gelijkspel</Muted>
				</div>

				{/* Away Team Wins */}
				<div className="text-center flex flex-col items-center gap-y-2">
					<span className="bg-black-500 shadow-[inset_0_0_0_2px_#1C1A27] py-1 px-2.5 inline-block rounded-md">
						<Label className="text-white leading-none text-base lg:text-base">{summary.awayWins}</Label>
					</span>
					<Muted>Overwinningen</Muted>
					<Muted className="-mt-1">{awayTeamName}</Muted>
				</div>
			</div>

			{/* Win Percentage Bars (optional visual enhancement) */}
			{summary.totalMatches > 0 && (
				<div className="mt-4 max-w-2xl m-auto">
					<div className="flex justify-center h-2 rounded-md overflow-hidden">
						<div
							className="bg-red-500"
							style={{ width: `${(summary.homeWins / summary.totalMatches) * 100}%` }}
						/>
						<div
							className="bg-gray-400"
							style={{ width: `${(summary.draws / summary.totalMatches) * 100}%` }}
						/>
						<div
							className="bg-black-500 rounded-r-md"
							style={{ width: `${(summary.awayWins / summary.totalMatches) * 100}%` }}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
